// frontend/src/hooks/useTokenAutoRefresh.ts
import { useEffect, useRef } from 'react';
import { useAuth } from './useAuth';
import { getTokenExpiry, isTokenExpired } from '../api/auth';

/**
 * Sliding-session token auto-refresh for active users
 * - Refreshes a few minutes before expiry
 * - Gated by recent activity and tab visibility
 * - Deduplicates concurrent refresh attempts
 */
export function useTokenAutoRefresh() {
  const { token, refreshToken, logout, hasHydrated } = useAuth();

  // Tunables
  const REFRESH_AHEAD_MS = 5 * 60 * 1000; // 5 minutes
  const SKEW_MS = 90 * 1000; // 90s buffer for clock skew
  const ACTIVITY_WINDOW_MS = 15 * 60 * 1000; // user activity considered recent within 15m
  const MIN_REFRESH_INTERVAL_MS = 10 * 60 * 1000; // do not refresh more frequently than every 10m
  const URGENT_THRESHOLD_MS = 60 * 1000; // force refresh when <60s left
  const VISIBILITY_REQUIRED = true; // only refresh when tab is visible

  const timerRef = useRef<number | null>(null);
  const lastActivityAtRef = useRef<number>(Date.now());
  const lastRefreshAtRef = useRef<number>(0);
  const refreshInFlightRef = useRef<Promise<boolean> | null>(null);

  // Record user activity
  useEffect(() => {
    const record = () => {
      lastActivityAtRef.current = Date.now();
    };
    const onVisibility = () => {
      // On becoming visible, attempt an immediate check if near expiry
      scheduleOrRunImmediate();
    };
    const onOnline = () => {
      scheduleOrRunImmediate();
    };

    window.addEventListener('mousemove', record, { passive: true });
    window.addEventListener('keydown', record, { passive: true });
    window.addEventListener('click', record, { passive: true });
    window.addEventListener('scroll', record, { passive: true });
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('online', onOnline);

    return () => {
      window.removeEventListener('mousemove', record as any);
      window.removeEventListener('keydown', record as any);
      window.removeEventListener('click', record as any);
      window.removeEventListener('scroll', record as any);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('online', onOnline);
    };
  }, []);

  // Clear any scheduled timer
  const clearTimer = () => {
    if (timerRef.current) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  // Core refresh logic
  const ensureFreshToken = async () => {
    if (!token) return;
    const now = Date.now();
    const expiry = getTokenExpiry(token);

    if (!expiry) {
      // Unknown expiry: avoid aggressive client-side logout. Rely on server 401.
      return;
    }

    const timeLeft = expiry - now;

    // If already expired (or considered so), attempt a last-chance refresh
    // before logging the user out. This avoids false positives from small
    // clock skews and gives the backend a chance to issue a new token.
    if (timeLeft <= 0 || isTokenExpired(token)) {
      console.warn('Token appears expired during refresh check; attempting grace refresh');
      try {
        const ok = await refreshToken();
        if (ok) {
          lastRefreshAtRef.current = Date.now();
          console.debug('Grace refresh succeeded; keeping session');
          return; // token updated; normal schedule will re-evaluate
        }
      } catch (e) {
        console.warn('Grace refresh attempt failed', e);
      }
      console.warn('Token confirmed expired after grace refresh; logging out');
      logout('expired');
      return;
    }

    // Visibility gate
    if (VISIBILITY_REQUIRED && typeof document !== 'undefined' && document.hidden) {
      // Will re-check on visibilitychange
      return;
    }

    // Activity gate
    if (now - lastActivityAtRef.current > ACTIVITY_WINDOW_MS) {
      // Inactive user; do not refresh. Next activity will re-check.
      return;
    }

    // Min interval gate (unless urgent)
    const sinceLastRefresh = now - lastRefreshAtRef.current;
    const urgent = timeLeft < URGENT_THRESHOLD_MS;
    if (!urgent && sinceLastRefresh < MIN_REFRESH_INTERVAL_MS) {
      return;
    }

    // Deduplicate
    if (refreshInFlightRef.current) {
      try {
        await refreshInFlightRef.current;
      } finally {
        return;
      }
    }

    const op = (async () => {
      try {
        const ok = await refreshToken();
        if (ok) {
          lastRefreshAtRef.current = Date.now();
          console.debug('Token auto-refreshed successfully');
        } else {
          // If refresh failed, check if token is now expired; if so, logout
          const stillToken = token; // capture
          if (stillToken && isTokenExpired(stillToken)) {
            logout('expired');
          }
        }
        return ok;
      } catch (e) {
        console.warn('Token auto-refresh failed', e);
        return false;
      }
    })();

    refreshInFlightRef.current = op;
    try {
      await op;
    } finally {
      refreshInFlightRef.current = null;
    }
  };

  const scheduleOrRunImmediate = () => {
    clearTimer();
    if (!token) return;
    const expiry = getTokenExpiry(token);
    if (!expiry) return;
    const now = Date.now();
    const timeLeft = expiry - now;

    // If within refresh-ahead window, try now; otherwise schedule
    const threshold = REFRESH_AHEAD_MS + SKEW_MS;
    if (timeLeft <= threshold) {
      // Run soon but allow microtask queue to settle
      timerRef.current = window.setTimeout(() => { void ensureFreshToken(); }, 0);
    } else {
      const timeoutMs = Math.max(timeLeft - threshold, 0);
      timerRef.current = window.setTimeout(() => { void ensureFreshToken(); }, timeoutMs);
    }
  };

  // Respond to token changes/hydration
  useEffect(() => {
    clearTimer();
    if (!hasHydrated || !token) return;
    scheduleOrRunImmediate();
    return () => clearTimer();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, hasHydrated]);
}
